/**
 * Money.
 *
 * Every amount in RentorNG is an integer count of the currency's minor unit — kobo, cents,
 * pesewas — carried as a JavaScript `number` in the domain layer and a `bigint` column in the
 * database. There are no floats. A tenancy is someone's home and someone else's income; an
 * amount that is 0.1 out because of binary floating point is not a rounding artefact, it is a
 * dispute.
 *
 * Percentages are basis points (1% = 100 bp), also integers, for the same reason.
 */

export type Minor = number; // integer minor units
export type BasisPoints = number; // 10_000 bp = 100%

export interface CurrencyInfo {
  code: string;
  symbol: string;
  decimals: number;
  /** Where the symbol sits relative to the number. */
  position: "before" | "after";
}

/**
 * The currencies the product ships with. Deliberately a plain table rather than
 * `Intl.NumberFormat` alone: we need the minor-unit exponent for arithmetic, and we want the
 * symbol we choose rather than the one the runtime's locale data happens to carry.
 */
export const CURRENCIES: Record<string, CurrencyInfo> = {
  NGN: { code: "NGN", symbol: "₦", decimals: 2, position: "before" },
  GHS: { code: "GHS", symbol: "GH₵", decimals: 2, position: "before" },
  KES: { code: "KES", symbol: "KSh", decimals: 2, position: "before" },
  ZAR: { code: "ZAR", symbol: "R", decimals: 2, position: "before" },
  UGX: { code: "UGX", symbol: "USh", decimals: 0, position: "before" },
  TZS: { code: "TZS", symbol: "TSh", decimals: 2, position: "before" },
  RWF: { code: "RWF", symbol: "FRw", decimals: 0, position: "before" },
  XOF: { code: "XOF", symbol: "CFA", decimals: 0, position: "before" },
  XAF: { code: "XAF", symbol: "FCFA", decimals: 0, position: "before" },
  EGP: { code: "EGP", symbol: "E£", decimals: 2, position: "before" },
  MAD: { code: "MAD", symbol: "DH", decimals: 2, position: "after" },
  USD: { code: "USD", symbol: "$", decimals: 2, position: "before" },
  GBP: { code: "GBP", symbol: "£", decimals: 2, position: "before" },
  EUR: { code: "EUR", symbol: "€", decimals: 2, position: "before" },
};

export const DEFAULT_CURRENCY = "NGN";

export function currencyInfo(code: string): CurrencyInfo {
  return CURRENCIES[code] ?? { code, symbol: code + " ", decimals: 2, position: "before" };
}

/** `1500.50` in NGN becomes `150050`. Rounds half away from zero, once, at the boundary. */
export function toMinor(major: number | string, currency = DEFAULT_CURRENCY): Minor {
  const { decimals } = currencyInfo(currency);
  const n = typeof major === "string" ? Number(major.replace(/[,\s]/g, "")) : major;
  if (!Number.isFinite(n)) throw new Error(`Not an amount: ${String(major)}`);
  const scaled = n * 10 ** decimals;
  return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
}

export function toMajor(minor: Minor, currency = DEFAULT_CURRENCY): number {
  return minor / 10 ** currencyInfo(currency).decimals;
}

/**
 * `formatMoney(150050, "NGN")` gives `₦1,500.50`.
 *
 * `compact` gives `₦1.5m` for dashboard tiles, where a nine-digit annual rent otherwise
 * wraps and stops being scannable. Compact is never used on an invoice, a receipt or a
 * statement — only where the exact figure is one click away.
 */
export function formatMoney(
  minor: Minor,
  currency = DEFAULT_CURRENCY,
  opts: { compact?: boolean; showSymbol?: boolean; signed?: boolean } = {},
): string {
  const info = currencyInfo(currency);
  const negative = minor < 0;
  const abs = Math.abs(minor);
  let body: string;

  if (opts.compact) {
    const major = abs / 10 ** info.decimals;
    const units: Array<[number, string]> = [
      [1e9, "b"],
      [1e6, "m"],
      [1e3, "k"],
    ];
    const hit = units.find(([size]) => major >= size);
    body = hit
      ? trimZeros((major / hit[0]).toFixed(major / hit[0] >= 100 ? 0 : 1)) + hit[1]
      : groupDigits(major.toFixed(info.decimals));
  } else {
    body = groupDigits((abs / 10 ** info.decimals).toFixed(info.decimals));
  }

  const symbol = opts.showSymbol === false ? "" : info.symbol;
  const withSymbol = info.position === "before" ? symbol + body : body + " " + symbol;
  if (negative) return "-" + withSymbol;
  if (opts.signed && minor > 0) return "+" + withSymbol;
  return withSymbol;
}

function trimZeros(s: string): string {
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
}

function groupDigits(s: string): string {
  const [whole, frac] = s.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac ? `${grouped}.${frac}` : grouped;
}

/** Parse whatever a human typed into a money field. Accepts `1,500.50`, `₦1500`, `1 500`. */
export function parseMoney(input: string, currency = DEFAULT_CURRENCY): Minor | null {
  const cleaned = input.replace(/[^0-9.\-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return toMinor(n, currency);
}

export const sum = (amounts: Minor[]): Minor => amounts.reduce((a, b) => a + b, 0);

/** A percentage of an amount, in basis points, rounded half away from zero. */
export function applyRate(amount: Minor, bp: BasisPoints): Minor {
  const raw = (amount * bp) / 10_000;
  return raw < 0 ? -Math.round(-raw) : Math.round(raw);
}

export function formatRate(bp: BasisPoints): string {
  const pct = bp / 100;
  return `${trimZeros(pct.toFixed(2))}%`;
}

export function parseRate(input: string): BasisPoints | null {
  const n = Number(input.replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * Split an amount into `parts` pieces that add back up to exactly the original.
 *
 * The remainder is distributed one minor unit at a time to the earliest parts, so twelve
 * monthly instalments of an annual rent that does not divide by twelve are correct to the last
 * kobo and the tenant never sees an unexplained one-unit difference on the final instalment.
 */
export function splitEvenly(amount: Minor, parts: number): Minor[] {
  if (parts <= 0) throw new Error("Cannot split into fewer than one part");
  const base = Math.trunc(amount / parts);
  let remainder = amount - base * parts;
  const step = remainder >= 0 ? 1 : -1;
  return Array.from({ length: parts }, () => {
    if (remainder !== 0) {
      remainder -= step;
      return base + step;
    }
    return base;
  });
}

/** Pro-rata for a partial period, by day count. Used for mid-cycle move-ins and move-outs. */
export function proRata(fullAmount: Minor, daysCharged: number, daysInPeriod: number): Minor {
  if (daysInPeriod <= 0) return 0;
  const raw = (fullAmount * daysCharged) / daysInPeriod;
  return raw < 0 ? -Math.round(-raw) : Math.round(raw);
}

/** Amount in words — required on receipts and tenancy agreements in most jurisdictions. */
export function amountInWords(minor: Minor, currency = DEFAULT_CURRENCY): string {
  const info = currencyInfo(currency);
  const abs = Math.abs(minor);
  const whole = Math.trunc(abs / 10 ** info.decimals);
  const frac = abs - whole * 10 ** info.decimals;
  const names: Record<string, [string, string]> = {
    NGN: ["naira", "kobo"],
    GHS: ["cedis", "pesewas"],
    KES: ["shillings", "cents"],
    ZAR: ["rand", "cents"],
    USD: ["dollars", "cents"],
    GBP: ["pounds", "pence"],
    EUR: ["euro", "cents"],
  };
  const [major, minorName] = names[currency] ?? [currency, "cents"];
  const head = `${numberToWords(whole)} ${major}`;
  const tail = frac > 0 ? ` and ${numberToWords(frac)} ${minorName}` : "";
  const sign = minor < 0 ? "minus " : "";
  return capitalise(`${sign}${head}${tail} only`);
}

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALES: Array<[number, string]> = [
  [1e12, "trillion"],
  [1e9, "billion"],
  [1e6, "million"],
  [1e3, "thousand"],
];

export function numberToWords(n: number): string {
  if (!Number.isFinite(n)) throw new Error("Not a number");
  if (n < 0) return "minus " + numberToWords(-n);
  if (n < 20) return ONES[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const r = n % 10;
    return TENS[t] + (r ? "-" + ONES[r] : "");
  }
  if (n < 1000) {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return ONES[h] + " hundred" + (r ? " and " + numberToWords(r) : "");
  }
  for (const [size, name] of SCALES) {
    if (n >= size) {
      const count = Math.floor(n / size);
      const r = n % size;
      const joiner = r === 0 ? "" : r < 100 ? " and " : " ";
      return numberToWords(count) + " " + name + joiner + (r ? numberToWords(r) : "");
    }
  }
  return String(n);
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * What a money field should show while it is being typed, and what should be submitted.
 *
 * Grouping as you type is safe here for a reason worth stating: **this product groups with a
 * comma and separates decimals with a dot for every currency it supports** — see `groupDigits`
 * above — so there is no locale in which the two characters swap meaning. A field that showed
 * `1.234,56` for one currency and `1,234.56` for another would be a live hazard, because
 * `parseMoney` strips commas and keeps dots: the first would silently become one and a bit.
 *
 * What varies by currency is the number of decimals, and only that. UGX, RWF, XOF and XAF have
 * none, so their fields refuse a decimal point outright rather than accepting one and rounding it
 * away somewhere the person cannot see.
 *
 * Two values come back:
 *
 * - `display` — grouped, for the box.
 * - `canonical` — digits and at most one dot, for the hidden field that is actually submitted.
 *   The server never has to guess what a separator meant, because it never receives one.
 *
 * A part-typed amount survives: a trailing dot stays while the fraction is still being entered,
 * and `1.50` does not collapse to `1.5` under the caret.
 */
export function formatAmountInput(raw: string, currency = DEFAULT_CURRENCY): { display: string; canonical: string } {
  const { decimals } = currencyInfo(currency);

  const negative = raw.trim().startsWith("-");
  const cleaned = raw.replace(/[^\d.]/g, "");

  const firstDot = cleaned.indexOf(".");
  let whole = firstDot === -1 ? cleaned : cleaned.slice(0, firstDot);
  // Everything after the first dot, with any further dots dropped: "1.2.3" is one amount typed
  // badly, not two, and rejecting it outright loses what was already entered.
  let fraction = firstDot === -1 ? null : cleaned.slice(firstDot + 1).replace(/\./g, "");

  if (decimals === 0) fraction = null;
  else if (fraction !== null) fraction = fraction.slice(0, decimals);

  // Leading zeros are noise, but "0" and "0.5" must survive being typed.
  whole = whole.replace(/^0+(?=\d)/, "");

  const sign = negative ? "-" : "";
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const tail = fraction === null ? "" : `.${fraction}`;

  if (!whole && fraction === null) return { display: sign, canonical: sign };
  return { display: `${sign}${grouped}${tail}`, canonical: `${sign}${whole}${tail}` };
}
